/**
 * GlobalFilterContext — Shared state for date range and store filters.
 *
 * Provides a React context so all layer pages and the Command Center
 * can consume and set the same filter state. Syncs with URL search
 * params (?from=&to=&store=) for shareable links.
 *
 * @module contexts/GlobalFilterContext
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import type { DateRange } from '@/types';

// ============================================================
// Types
// ============================================================

export interface GlobalFilters {
  dateRange: DateRange;
  storeId: string | null;
}

interface GlobalFilterContextValue {
  filters: GlobalFilters;
  setDateRange: (range: DateRange) => void;
  setStoreId: (storeId: string | null) => void;
  clearFilters: () => void;
  /** Human-readable date label: "7d", "30d", "90d", or "Personalizado" */
  activeDateLabel: string;
}

// ============================================================
// Defaults
// ============================================================

function getDefaultDateRange(): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

const DEFAULT_FILTERS: GlobalFilters = {
  dateRange: getDefaultDateRange(),
  storeId: null,
};

// ============================================================
// URL helpers
// ============================================================

function filtersFromURL(): GlobalFilters {
  if (typeof window === 'undefined') return DEFAULT_FILTERS;

  const params = new URLSearchParams(window.location.search);
  const from = params.get('from');
  const to = params.get('to');
  const store = params.get('store');

  const defaultRange = getDefaultDateRange();

  return {
    dateRange: {
      start: from || defaultRange.start,
      end: to || defaultRange.end,
    },
    storeId: store || null,
  };
}

function filtersToURL(filters: GlobalFilters): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const defaultRange = getDefaultDateRange();

  // Only set non-default values
  if (filters.dateRange.start !== defaultRange.start || filters.dateRange.end !== defaultRange.end) {
    params.set('from', filters.dateRange.start);
    params.set('to', filters.dateRange.end);
  } else {
    params.delete('from');
    params.delete('to');
  }

  if (filters.storeId) {
    params.set('store', filters.storeId);
  } else {
    params.delete('store');
  }

  const qs = params.toString();
  const newURL = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, '', newURL);
}

// ============================================================
// Date label computation
// ============================================================

function computeDateLabel(dateRange: DateRange): string {
  const start = new Date(dateRange.start);
  const end = new Date(dateRange.end);
  const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  // Check if end is today (within 1 day tolerance)
  const today = new Date();
  const endIsToday = Math.abs(end.getTime() - today.getTime()) < 2 * 86400000;

  if (endIsToday) {
    if (diffDays >= 6 && diffDays <= 8) return '7d';
    if (diffDays >= 29 && diffDays <= 31) return '30d';
    if (diffDays >= 89 && diffDays <= 91) return '90d';
  }
  return 'Personalizado';
}

// ============================================================
// Context
// ============================================================

const GlobalFilterContext = createContext<GlobalFilterContextValue | null>(null);

export const GlobalFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use URL as source of truth, with React state for reactivity
  const [filters, setFilters] = React.useState<GlobalFilters>(filtersFromURL);

  const setDateRange = useCallback((range: DateRange) => {
    setFilters(prev => {
      const next = { ...prev, dateRange: range };
      filtersToURL(next);
      return next;
    });
  }, []);

  const setStoreId = useCallback((storeId: string | null) => {
    setFilters(prev => {
      const next = { ...prev, storeId };
      filtersToURL(next);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    const next = { dateRange: getDefaultDateRange(), storeId: null };
    setFilters(next);
    filtersToURL(next);
  }, []);

  const activeDateLabel = useMemo(() => computeDateLabel(filters.dateRange), [filters.dateRange]);

  const value = useMemo<GlobalFilterContextValue>(() => ({
    filters,
    setDateRange,
    setStoreId,
    clearFilters,
    activeDateLabel,
  }), [filters, setDateRange, setStoreId, clearFilters, activeDateLabel]);

  return (
    <GlobalFilterContext.Provider value={value}>
      {children}
    </GlobalFilterContext.Provider>
  );
};

export function useGlobalFilters(): GlobalFilterContextValue {
  const ctx = useContext(GlobalFilterContext);
  if (!ctx) {
    throw new Error('useGlobalFilters must be used within a GlobalFilterProvider');
  }
  return ctx;
}

export default GlobalFilterContext;
